import { useState } from "react";
import { Search, Wifi, WifiOff, RefreshCw } from "lucide-react";
import { useLiveStandings } from "@/hooks/useLiveStandings";
import { StandingsTable } from "@/components/StandingsTable";
import { EmptyState, ErrorState, Loader, OfficialBadge } from "@/components/common";
import { fmtDateTime } from "@/lib/format";
import { ListOrdered } from "lucide-react";

function ConnIndicator({ conn, received }) {
  const live = conn === "live";
  return (
    <div data-testid="live-connection-status" className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-[#C6C6CE]/70">
      {live ? <Wifi className="w-3.5 h-3.5 text-emerald-400" /> : <WifiOff className="w-3.5 h-3.5 text-amber-300" />}
      <span>{live ? "Realtime attivo" : conn === "connecting" ? "Connessione..." : "Riconnessione in corso"}</span>
      {received && <span className="text-[#C6C6CE]/40 normal-case tracking-normal" data-testid="last-received">· ricevuto {received.toLocaleTimeString("it-IT")}</span>}
    </div>
  );
}

const sel = "nx-input py-1.5 text-xs w-auto";

export function LiveStandings({ slug }) {
  const [filters, setFilters] = useState({});
  const [q, setQ] = useState("");
  const [view, setView] = useState("auto");
  const { data, error, conn, received, reload } = useLiveStandings(slug, filters);
  const set = (k, v) => setFilters((f) => { const n = { ...f }; if (v) n[k] = v; else delete n[k]; return n; });

  const final = data?.final;
  const showOfficial = final && !data?.event?.correction_open && view !== "live" && !Object.keys(filters).length;
  const source = showOfficial ? final.rows : data?.rows || [];
  const s = q.trim().toLowerCase();
  const rows = !s ? source : source.filter((r) => r.name.toLowerCase().includes(s) || (r.tag || "").toLowerCase().includes(s) || (r.players || []).some((p) => p.toLowerCase().includes(s)));

  if (error && !data) return <ErrorState message={error} onRetry={reload} />;
  if (!data) return <Loader label="Caricamento classifica..." />;
  const f = data.filters;
  return (
    <div data-testid="live-standings">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex flex-wrap items-center gap-3">
          <OfficialBadge official={!!showOfficial} />
          {showOfficial ? (
            <span className="text-xs text-[#C6C6CE]/60">Versione {final.version} · pubblicata {fmtDateTime(final.published_at)}</span>
          ) : (
            <span data-testid="standings-updated-at" className="text-xs text-[#C6C6CE]/60">Ultimo aggiornamento: {fmtDateTime(data.updated_at)}</span>
          )}
          {final && !data.event.correction_open && (
            <button data-testid="toggle-official-live" onClick={() => setView(showOfficial ? "live" : "auto")} className="btn-ghost btn-sm">
              {showOfficial ? "Vedi dati live" : "Vedi ufficiale"}
            </button>
          )}
          {data.event.correction_open && <span className="text-xs text-amber-200">Correzione amministrativa in corso</span>}
        </div>
        <div className="flex items-center gap-3">
          <ConnIndicator conn={conn} received={received} />
          <button data-testid="standings-refresh" onClick={reload} aria-label="Aggiorna" className="btn-ghost btn-sm px-2"><RefreshCw className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {error && <div className="text-xs text-amber-200 mb-3" data-testid="standings-soft-error">{error} — mostro gli ultimi dati ricevuti.</div>}
      <div className="flex flex-wrap gap-2.5 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#C6C6CE]/40" />
          <input data-testid="standings-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca clan, squadra o giocatore" className="nx-input pl-9 py-1.5 text-xs" />
        </div>
        <select data-testid="filter-day" aria-label="Giornata" className={sel} value={filters.day || ""} onChange={(e) => set("day", e.target.value)}>
          <option value="">Tutte le giornate</option>{f.days.map((d) => <option key={d} value={d}>Giornata {d}</option>)}
        </select>
        <select data-testid="filter-round" aria-label="Turno" className={sel} value={filters.round || ""} onChange={(e) => set("round", e.target.value)}>
          <option value="">Tutti i turni</option>{f.rounds.map((d) => <option key={d} value={d}>Turno {d}</option>)}
        </select>
        <select data-testid="filter-phase" aria-label="Fase" className={sel} value={filters.phase || ""} onChange={(e) => set("phase", e.target.value)}>
          <option value="">Tutte le fasi</option>{f.phases.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <input data-testid="filter-date-from" type="date" aria-label="Dal" className={sel} value={(filters.date_from || "").slice(0, 10)} onChange={(e) => set("date_from", e.target.value)} />
        <input data-testid="filter-date-to" type="date" aria-label="Al" className={sel} value={(filters.date_to || "").slice(0, 10)} onChange={(e) => set("date_to", e.target.value ? `${e.target.value}T23:59:59` : "")} />
      </div>
      {rows.length ? (
        <StandingsTable rows={rows} extraStats={data.event.score_rules?.extra_stats || []} showChange={!showOfficial && !Object.keys(filters).length} showPrize={!!showOfficial} />
      ) : (
        <EmptyState icon={ListOrdered} title="Nessun dato in classifica" text={q ? "Nessun risultato per la ricerca." : "La classifica comparirà quando saranno registrati partecipanti e risultati."} />
      )}
    </div>
  );
}
